import { useState, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import AuthContext from 'store/auth-context'
import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import { Box } from '@mui/material'

import Input from 'components/common/BaseInput/BaseInput'
import Text from 'components/common/BaseText/BaseText'
import Button from 'components/common/BaseButton/BaseButton'
import Spinner from 'components/Spinner/Spinner'
import capitalizeFirstLetter from 'utils/capitalizeFirstLetter'
import codeToText from 'utils/codeToText'
import { URLS, ERRORS } from 'constants/index'

import { AuthFormInputsInterface } from './AuthForm.types'

import './AuthForm.scss'

const AuthForm: React.FC = (): JSX.Element => {
	const history = useHistory()

	const authCtx = useContext(AuthContext)

	const [emailError, setEmailError] = useState<string>()
	const [passwordError, setPasswordError] = useState<string>()
	const [isLoginForm, setIsLoginForm] = useState(true)
	const [isLoading, setIsLoading] = useState(false)

	const { handleSubmit, control, reset } = useForm<AuthFormInputsInterface>()

	const changeFormTypeHandler = () => {
		setIsLoginForm(prevState => !prevState)
	}

	const onSubmit: SubmitHandler<AuthFormInputsInterface> = ({ email, password, passwordConfirmation }) => {
		if (!isLoginForm && password !== passwordConfirmation) {
			setPasswordError('Passwords should be the same')

			return
		}

		setIsLoading(true)
		setEmailError('')
		setPasswordError('')

		fetch(isLoginForm ? URLS.signIn : URLS.signUp, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				email: email,
				password: password,
				returnSecureToken: true,
			}),
		})
			.then(res => {
				setIsLoading(false)

				if (res.ok) {
					reset()

					return res.json()
				} else {
					res.json().then(data => {
						const error = data?.error?.message

						switch (error) {
							case ERRORS.invalidEmail:
							case ERRORS.emailExists:
							case ERRORS.missingEmail: {
								const errorMessage = capitalizeFirstLetter(codeToText(error).toLowerCase())
								setEmailError(errorMessage)
								break
							}

							case ERRORS.weakPassword: {
								const errorMessage = capitalizeFirstLetter(error.slice(error.lastIndexOf(':') + 1))
								setPasswordError(errorMessage)
								break
							}

							case ERRORS.invalidPassword:
							case ERRORS.missingPassword: {
								const errorMessage = capitalizeFirstLetter(codeToText(error).toLowerCase())
								setPasswordError(errorMessage)
								break
							}

							default: {
								setEmailError('Invalid e-mail or password')
								setPasswordError('Invalid e-mail or password')
							}
						}
					})
				}
			})
			.then(data => {
				const expirationTime = new Date(new Date().getTime() + data?.expiresIn * 1000)
				const expirationTimeISOString = expirationTime.toISOString()

				authCtx.login(data?.idToken, expirationTimeISOString)

				history.replace('/')
			})
	}

	if (isLoading) {
		return <Spinner />
	}

	return (
		<Box className='auth-form' component='form'>
			<h3 className='auth-form__title'>{isLoginForm ? 'Login' : 'Register'}</h3>

			<p className='auth-form__subtitle'>
				{isLoginForm ? 'Lorem ipsum dolor sit amet.' : 'Lorem ipsum dolor sit amet.'}
			</p>

			<Controller
				name='email'
				control={control}
				defaultValue=''
				render={({ field: { onChange, value } }) => (
					<Input
						sx={{
							width: '100%',
							borderRadius: '3rem',
						}}
						required
						id='email'
						label='E-mail'
						type='email'
						onChange={onChange}
						value={value}
					/>
				)}
			/>
			{!!emailError && <Text sx={{ color: 'red' }}>{emailError}</Text>}

			<Controller
				name='password'
				control={control}
				defaultValue=''
				render={({ field: { onChange, value } }) => (
					<Input
						sx={{
							width: '100%',
							borderRadius: '3rem',
						}}
						required
						id='password'
						label='Password'
						type='password'
						onChange={onChange}
						value={value}
					/>
				)}
			/>
			{!!passwordError && <Text sx={{ color: 'red' }}>{passwordError}</Text>}

			{!isLoginForm && (
				<Controller
					name='passwordConfirmation'
					control={control}
					defaultValue=''
					render={({ field: { onChange, value } }) => (
						<Input
							sx={{
								width: '100%',
								borderRadius: '3rem',
							}}
							required
							id='password-confirmation'
							label='Confirm password'
							type='password'
							onChange={onChange}
							value={value}
						/>
					)}
				/>
			)}
			{!!passwordError && <Text sx={{ color: 'red' }}>{passwordError}</Text>}

			<Button sx={{ marginTop: '.5rem ' }} type='contained' onClick={handleSubmit(onSubmit)}>
				{isLoginForm ? 'Login' : 'Register'}
			</Button>

			<Button type='text' onClick={changeFormTypeHandler}>
				{isLoginForm ? 'Create new account' : 'Login to exisiting account'}
			</Button>
		</Box>
	)
}

export default AuthForm
